import { useNavigate } from "react-router-dom";

import {
  useApproveBatch,
  useDeleteBatch,
  useEditBatch,
  useSendBatchForReview,
} from "hooks/api/use-batches";

import { Route as AppRoute } from "utilities/app-routes";
import { toApiDate } from "utilities/date";

interface UseBatchActionMenuItemsProps {
  clientId: string | undefined;
  currentBatch: { id: string } | null;
  fetchBatch: () => Promise<void>;
  setIEditBatchModalOpen: (isOpen: boolean) => void;
}

type EditBatchFormValues = {
  name?: string;
  description?: string;
  dueDate?: string | Date;
  mondayBoardLink?: string;
};

const useBatchActionMenuItems = ({
  clientId,
  currentBatch,
  fetchBatch,
  setIEditBatchModalOpen,
}: UseBatchActionMenuItemsProps) => {
  const navigate = useNavigate();

  const approveBatchMutation = useApproveBatch({ clientId });
  const sendBatchForReviewMutation = useSendBatchForReview({ clientId });
  const editBatchMutation = useEditBatch({ clientId });
  const deleteBatchMutation = useDeleteBatch({ clientId });

  const handleApproveBatch = async () => {
    if (!clientId || !currentBatch?.id) return;

    await approveBatchMutation.mutateAsync({ batchId: currentBatch.id });

    // approveBatch returns batch metadata (a BatchItem), not a touchpoint version.
    // Refresh the batch via fetchBatch instead of writing it into selectedVersion,
    // which would clobber the touchpoint selection used by the version modals.
    await fetchBatch();
  };

  const handleSendForReview = async () => {
    if (!clientId || !currentBatch?.id) return;

    await sendBatchForReviewMutation.mutateAsync({ batchId: currentBatch.id });
    await fetchBatch();
  };

  const handleEditBatch = async (values: EditBatchFormValues) => {
    if (!clientId || !currentBatch?.id) return;
    if (!values.dueDate) return;

    const dueDate = toApiDate(values.dueDate);

    await editBatchMutation.mutateAsync({
      batchId: currentBatch.id,
      attributes: {
        ...values,
        dueDate,
      },
    });

    setIEditBatchModalOpen(false);

    fetchBatch();
  };

  const handleDeleteBatch = async () => {
    if (!clientId || !currentBatch?.id) return;

    await deleteBatchMutation.mutateAsync({ batchId: currentBatch.id });
    const link = `${AppRoute.clients}/${clientId}/${AppRoute.approvalQueue}`;

    navigate(link);
  };

  return {
    handleApproveBatch,
    handleSendForReview,
    handleEditBatch,
    handleDeleteBatch,
  };
};

export default useBatchActionMenuItems;
